'use client';

import { ConfigProvider, Pagination, Select } from 'antd';
import React, { useState, useTransition } from 'react';

import { AppColors } from '../../theme';
import styles from './pagination-control.styles';

/**
 * Define la estructura de una opción de tamaño de página en el selector desplegable.
 */
export type PageSizeOption = {
  /**
   * El valor numérico del tamaño de la página (p. Ej., 10, 25, 50).
   */
  value: number;
  /**
   * La etiqueta de cadena que se mostrará para la opción (p. Ej., '10', '25', '50').
   */
  label: string;
};

/**
 * Props para el componente PaginationControl.
 */
type PaginationControlProps = {
  /**
   * El número de página actualmente activo.
   */
  current: number;
  /**
   * El número de elementos que se mostrarán por página.
   */
  pageSize: number;
  /**
   * El número total de elementos en todas las páginas.
   */
  totalItems: number;
  /**
   * Una función de devolución de llamada que se ejecuta cuando cambia el número de página.
   *
   * @param {number} page - El nuevo número de página.
   */
  onPageChange?: (page: number) => void;
  /**
   * Una función de devolución de llamada que se ejecuta cuando cambia el tamaño de la página.
   *
   * @param {number} pageSize - El nuevo tamaño de página.
   */
  onPageSizeChange?: (pageSize: number) => void;
  /**
   * Un array de opciones para el selector de tamaño de página.
   *
   * @default [{ value: 10, label: '10' }, { value: 25, label: '25' }, { value: 50, label: '50' }, { value: 100, label: '100' }]
   */
  pageSizeOptions?: PageSizeOption[];
  /**
   * Si es verdadero, la etiqueta mostrará el número total de elementos (p. Ej., "Mostrando 1-10 de 100").
   * Si es falso, solo mostrará el rango (p. Ej., "Mostrando 1-10").
   *
   * @default false
   */
  showTotalItems?: boolean;
};

const DEFAULT_PAGE_SIZE_OPTIONS: PageSizeOption[] = [
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' }
];

/**
 * Un componente de control de paginación completo que incluye un selector de tamaño de página.
 * Envuelve los componentes de paginación y selección de Ant Design con un estilo y diseño personalizados.
 *
 * @param {PaginationControlProps} props - Las props para el componente PaginationControl.
 *
 * @returns {JSX.Element} El componente PaginationControl renderizado.
 */
function PaginationControl({
  current,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  showTotalItems = false
}: PaginationControlProps) {
  const [isPending] = useTransition();
  const [localPageSize, setLocalPageSize] = useState(pageSize);

  const handlePageSizeChange = async (size: number) => {
    setLocalPageSize(size);
    onPageSizeChange?.(size);
  };

  const handlePageChange = async (page: number) => {
    if (page !== current) {
      onPageChange?.(page);
    }
  };

  const startItem = (current - 1) * localPageSize + 1;
  const endItem = Math.min(current * localPageSize, totalItems);
  const paginatorLabel = `Mostrando ${startItem}-${endItem} de ${totalItems}`;

  return (
    <ConfigProvider
      theme={{
        components: {
          Pagination: {
            itemActiveBg: AppColors.VA.NEUTRALS.WHITE,
            colorPrimary: AppColors.VA.PRIMARY._50_,
            colorPrimaryHover: AppColors.VA.PRIMARY._50_,
            colorText: AppColors.VA.PRIMARY._90_,
            itemBg: AppColors.VA.BACKGROUND._00_
          }
        }
      }}>
      <div style={styles.container}>
        <div style={styles.itemContainer}>
          <span style={styles.itemText}>{paginatorLabel}</span>
          <Select
            value={localPageSize}
            onChange={handlePageSizeChange}
            options={pageSizeOptions}
            style={styles.itemSelect}
            disabled={isPending}
          />
        </div>

        <Pagination
          current={current}
          total={totalItems}
          pageSize={localPageSize}
          onChange={handlePageChange}
          showSizeChanger={false}
          showQuickJumper={false}
          showLessItems={false}
          disabled={isPending}
          align='center'
        />
      </div>
    </ConfigProvider>
  );
}

export const dynamic = 'force-dynamic';
export const revalidate = 0;

export default PaginationControl;
